import { useState } from "react";
import { formatStruktura, useStrukturaLabels } from "@/components/slot/MaterialSelect";
import { useHranyPodlaHrubky, useSlotEdgesByThickness } from "@/api/hooks/useSlotEdges";
import type { OknoZmenyHran } from "@/components/slot/ZmenaHranSlotuDialog";
import { HranaPickerDialog } from "@/components/dielec/HranyEditor";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import type { EdgeCandidateRow, MaterialSlot, MaterialSummary, SlotEdgeRow } from "@/api/schemas";
import { rozmerHrany } from "@/lib/format";

/**
 * Hrany materiálu zákazky — tabuľka podľa hrúbky dielca a detegované
 * hrany dekoru. Do KOLA 228 boli na spodku okna „Upraviť materiál M1“,
 * pod výberom z cenníka; user: *„musím hľadať automatické priradenie
 * hrán, keď ťuknem na materiál, a tam to je dole“*. Teraz ich ukazuje
 * okno Hrany (`SlotHranyDialog`) a okno Materiál len ako náhľad novej
 * dosky.
 */

/** Vráti najlepšie dostupné ID hrany (daex > hranipex > jafholz > twd > hafele). */
export function edgeIdOf(e: {
  daex_id?: string | null;
  hranipex_id?: string | null;
  jafholz_id?: string | null;
  twd_id?: string | null;
  hafele_id?: string | null;
}): string | null {
  return (
    e.daex_id ?? e.hranipex_id ?? e.jafholz_id ?? e.twd_id ?? e.hafele_id ?? null
  );
}

/**
 * Sklo podľa kategórie materiálu.
 *
 * Do KOLA 123 to držala fajka „Automaticky derivovať hrany", ktorá sa
 * pri skle sama odškrtla a tým schovala sekciu hrán. User: *„neviem,
 * načo tam je fajka, ktorú musíš zaškrtnúť, a volá sa to derivovať, čo
 * je blbosť — nechaj to zapnuté."* Skrývanie teda rozhoduje **druh
 * materiálu**, nie prepínač.
 */
export function jeSkloMaterial(kategoria: string | null | undefined): boolean {
  return kategoria === "SKLO" || kategoria === "SKLO_KALENIE";
}

// ─── Hrany podľa hrúbky dielca ──────────────────────────────

/** Tabuľka "aké hrany sa priradia pre dielec hrúbky X". */
/** Zoskupený riadok — viacero hrúbok dielcov s rovnakými hranami. */
interface EdgeGroup {
  hrubky: number[];
  tenka: SlotEdgeRow["tenka"];
  hruba: SlotEdgeRow["hruba"];
  hruba2: SlotEdgeRow["hruba2"];
  /** True iba pre skupinu vytvorenú override-mi (každá hrúbka v skupine má override). */
  is_override: boolean;
  /** Stav bunky (KOLO 135): ok / nahradena / zla_sirka / chyba. */
  stavy: Record<EdgeSide, string>;
  ziadana_sirka_mm: number | null;
}

/** Rola hrany v tabuľke — tenká 0.5, hrubá 1 a hrubá 2 mm. */
type EdgeSide = "tenka" | "hruba" | "hruba2";

const SIDE_LABELS: Record<EdgeSide, string> = {
  tenka: "tenká",
  hruba: "hrubá",
  hruba2: "hrubá 2 mm",
};

/** Zoskupí riadky podľa identických hrán: (tenka_id, hruba_id, hruba2_id, is_override). */
function groupRowsByEdges(rows: SlotEdgeRow[]): EdgeGroup[] {
  const map = new Map<string, EdgeGroup>();
  for (const r of rows) {
    // Stav a šírka v kľúči — 18 mm (22, ok) a 36 mm (42, chýba) nesmú
    // splynúť len preto, že obom chýba tá istá hrana (KOLO 135)
    const stavy: Record<EdgeSide, string> = {
      tenka: r.stav_tenka ?? "ok",
      hruba: r.stav_hruba ?? "ok",
      hruba2: r.stav_hruba2 ?? "ok",
    };
    const key = `${r.tenka?.id ?? "_"}|${r.hruba?.id ?? "_"}|${r.hruba2?.id ?? "_"}|${r.is_override ? 1 : 0}|${stavy.tenka}|${stavy.hruba}|${stavy.hruba2}|${r.ziadana_sirka_mm ?? ""}`;
    let g = map.get(key);
    if (!g) {
      g = {
        hrubky: [],
        tenka: r.tenka,
        hruba: r.hruba,
        hruba2: r.hruba2,
        is_override: r.is_override,
        stavy,
        ziadana_sirka_mm: r.ziadana_sirka_mm ?? null,
      };
      map.set(key, g);
    }
    g.hrubky.push(r.hrubka_dielca_mm);
  }
  return Array.from(map.values()).sort(
    (a, b) => (a.hrubky[0] ?? 0) - (b.hrubky[0] ?? 0),
  );
}

/** Formátuje súvislý rozsah hrúbok do textu: "16–18 mm" alebo "10, 12, 16 mm". */
function formatHrubky(hrubky: number[]): string {
  if (hrubky.length === 0) return "—";
  const sorted = [...hrubky].sort((a, b) => a - b);
  // Skontroluj či sú všetky súvislé (rozdiel max 2-3mm)
  const isContiguous = sorted.every(
    (h, i) => i === 0 || h - (sorted[i - 1] ?? 0) <= 3,
  );
  if (sorted.length === 1) return `${sorted[0]} mm`;
  if (isContiguous && sorted.length >= 3) {
    return `${sorted[0]}–${sorted[sorted.length - 1]} mm`;
  }
  return `${sorted.join(", ")} mm`;
}

export function SlotEdgesByThicknessSection({
  slotId,
  materialId,
  spustiZmenuHran,
}: {
  slotId?: string;
  /** Bez slotu (pridávanie) — tabuľka len na pozretie (KOLO 135). */
  materialId?: string;
  /** Zmena tabuľky ide do dielcov cez okno so zoznamom (KOLO 222). */
  spustiZmenuHran?: OknoZmenyHran["spusti"];
}): JSX.Element {
  const slotQuery = useSlotEdgesByThickness(slotId);
  const materialQuery = useHranyPodlaHrubky(slotId ? undefined : materialId);
  const query = slotId ? slotQuery : materialQuery;
  const lenNaPozretie = !slotId || !spustiZmenuHran;
  const [editing, setEditing] = useState<{
    hrubky: number[];
    side: EdgeSide;
  } | null>(null);
  // "+ Pridať hrúbku" — hrúbka mimo auto zoznamu; po zadaní sa otvorí
  // výber hrubej hrany a uloží sa ako override (riadok pribudne).
  const [newHrubka, setNewHrubka] = useState("");

  const data = query.data?.data;

  function addHrubka(): void {
    const n = parseInt(newHrubka, 10);
    if (!n || n < 1 || n > 500) {
      toast({
        variant: "destructive",
        title: "Neplatná hrúbka",
        description: "Zadaj hrúbku dielca v mm (1–500).",
      });
      return;
    }
    if (data?.rows.some((r) => r.hrubka_dielca_mm === n)) {
      toast({ title: `Hrúbka ${n} mm už je v tabuľke` });
      setNewHrubka("");
      return;
    }
    setNewHrubka("");
    setEditing({ hrubky: [n], side: "hruba" });
  }

  async function clearOverrideGroup(hrubky: number[]): Promise<void> {
    if (!spustiZmenuHran) return;
    // Otázku nahradilo okno so zoznamom dielcov (KOLO 222); keď sa
    // dielcom nič nemení, uloží sa rovno — krok späť je Ctrl+Z
    const ulozene = await spustiZmenuHran(
      { zmazat_riadky: hrubky },
      `návrat hrán ${formatHrubky(hrubky)} na automatické`,
    );
    if (ulozene) toast({ title: `Hrany pre ${formatHrubky(hrubky)} sú zase automatické` });
  }

  if (query.isLoading) {
    return <p className="text-xs text-muted-foreground">Načítavam…</p>;
  }
  if (!data) return <></>;

  const groups = groupRowsByEdges(data.rows);
  const odkazy = odkazyNaHrany(data.rows);

  return (
    <div className="space-y-2">
    <SuhrnHran rows={data.rows} />
    <div className="rounded-md border">
      <div className="px-2.5 py-1.5 border-b flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Hrany podľa hrúbky dielca
          {/* Odkaz na hranu u dodávateľa — „že si to viem rovno
              skontrolovať" (KOLO 135); URL skladá backend */}
          {odkazy.map((o) => (
            <a
              key={o.odkaz}
              href={o.odkaz}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 normal-case tracking-normal text-primary hover:underline"
              title="Otvoriť hranu u dodávateľa"
            >
              {o.kod} ↗
            </a>
          ))}
        </p>
        <div className={lenNaPozretie ? "hidden" : "flex items-center gap-2"}>
          <p className="hidden sm:block text-[10px] text-muted-foreground">
            klik na hranu → nastaviť inú
          </p>
          <input
            type="number"
            min={1}
            max={500}
            value={newHrubka}
            onChange={(e) => setNewHrubka(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addHrubka();
              }
            }}
            placeholder="mm"
            aria-label="Nová hrúbka dielca"
            className="h-6 w-14 rounded border border-border px-1.5 text-[11px] font-mono focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            type="button"
            onClick={addHrubka}
            className="text-[11px] text-primary hover:underline whitespace-nowrap"
          >
            + Pridať hrúbku
          </button>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] text-muted-foreground uppercase">
            <tr>
              <th className="px-2 py-1 text-left w-44">Hrúbka dielca</th>
              <th className="px-2 py-1 text-left">Tenká (0.5 mm)</th>
              <th className="px-2 py-1 text-left">
                Hrubá ({data.hruba_hrana_mm} mm)
              </th>
              <th className="px-2 py-1 text-left">Hrubá (2 mm)</th>
              <th className="px-2 py-1 w-8"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {groups.map((g) => (
              <tr
                key={`${g.tenka?.id ?? "_"}|${g.hruba?.id ?? "_"}|${g.hruba2?.id ?? "_"}|${g.is_override ? 1 : 0}`}
                className={g.is_override ? "bg-amber-50" : ""}
              >
                <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                  {formatHrubky(g.hrubky)}
                  {g.is_override ? (
                    <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] text-amber-700">
                      manuál
                    </span>
                  ) : null}
                  <span
                    className="ml-1 text-[9px] text-muted-foreground"
                    title={g.hrubky.join(", ") + " mm"}
                  >
                    ({g.hrubky.length})
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <EdgeCell
                    edge={g.tenka}
                    stav={g.stavy.tenka}
                    ciel={CIEL_ROLY.tenka}
                    ziadanaSirka={g.ziadana_sirka_mm}
                    onClick={
                      lenNaPozretie
                        ? undefined
                        : () => setEditing({ hrubky: g.hrubky, side: "tenka" })
                    }
                  />
                </td>
                <td className="px-2 py-1.5">
                  <EdgeCell
                    edge={g.hruba}
                    stav={g.stavy.hruba}
                    ciel={CIEL_ROLY.hruba}
                    ziadanaSirka={g.ziadana_sirka_mm}
                    onClick={
                      lenNaPozretie
                        ? undefined
                        : () => setEditing({ hrubky: g.hrubky, side: "hruba" })
                    }
                  />
                </td>
                <td className="px-2 py-1.5">
                  <EdgeCell
                    edge={g.hruba2}
                    stav={g.stavy.hruba2}
                    ciel={CIEL_ROLY.hruba2}
                    ziadanaSirka={g.ziadana_sirka_mm}
                    onClick={
                      lenNaPozretie
                        ? undefined
                        : () => setEditing({ hrubky: g.hrubky, side: "hruba2" })
                    }
                  />
                </td>
                <td className="px-2 py-1.5">
                  {g.is_override ? (
                    <button
                      type="button"
                      onClick={() => void clearOverrideGroup(g.hrubky)}
                      className="text-[10px] text-muted-foreground hover:text-destructive"
                      title="Vrátiť skupinu na auto"
                    >
                      ↺
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {spustiZmenuHran ? (
        <EdgeOverrideEditor
          spustiZmenuHran={spustiZmenuHran}
          target={editing}
          currentRows={data.rows}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
    </div>
  );
}

/** Cieľová hrúbka roly v mm — do textu „0,5 mm v šírke 42 nie je". */
const CIEL_ROLY: Record<EdgeSide, string> = {
  tenka: "0,5",
  hruba: "1",
  hruba2: "2",
};

function rozmerHranyEdge(edge: MaterialSummary | null | undefined): string {
  return edge ? rozmerHrany(edge.sirka_mm, edge.hrubka_mm) : "?";
}

/** Rôzne odkazy na hrany v tabuľke (spravidla jedna rodina `HD 211381`). */
function odkazyNaHrany(rows: SlotEdgeRow[]): { kod: string; odkaz: string }[] {
  const out = new Map<string, string>();
  for (const r of rows) {
    for (const e of [r.tenka, r.hruba, r.hruba2]) {
      if (e?.eshop_odkaz && !out.has(e.eshop_odkaz)) {
        out.set(e.eshop_odkaz, e.hranipex_id?.replace(/ lep\.$/, "") ?? "Hranipex");
      }
    }
  }
  // Jedna rodina = jeden odkaz, aj keď ho majú viaceré rozmery
  const podlaKodu = new Map<string, string>();
  for (const [odkaz, kod] of out) {
    if (!podlaKodu.has(kod)) podlaKodu.set(kod, odkaz);
  }
  return [...podlaKodu].map(([kod, odkaz]) => ({ kod, odkaz }));
}

/**
 * Čo s hranami nesedí pre hrúbky, ktoré slot naozaj používa (KOLO 135).
 *
 * Nahrádza `ChybajuceHrubky`, ktorá pozerala hrúbku bez šírky: 0,5 mm
 * v 22 mm stačilo, aj keď 36 mm doska potrebuje 42 mm pásku.
 */
function SuhrnHran({ rows }: { rows: SlotEdgeRow[] }): JSX.Element | null {
  const vety: { text: string; chyba: boolean }[] = [];
  for (const r of rows) {
    if (!r.pouzita) continue;
    const roly: [EdgeSide, string, MaterialSummary | null | undefined][] = [
      ["tenka", r.stav_tenka ?? "ok", r.tenka],
      ["hruba", r.stav_hruba ?? "ok", r.hruba],
      ["hruba2", r.stav_hruba2 ?? "ok", r.hruba2],
    ];
    if (roly.every(([, st]) => st === "zla_sirka" || st === "chyba")) {
      vety.push({
        text: `Doska ${r.hrubka_dielca_mm} mm: dekor nemá hranu šírky ${r.ziadana_sirka_mm} mm — dielce s touto doskou budú svietiť.`,
        chyba: true,
      });
      continue;
    }
    for (const [rola, st, edge] of roly) {
      if (st === "nahradena") {
        vety.push({
          text: `Doska ${r.hrubka_dielca_mm} mm: ${CIEL_ROLY[rola]} mm v šírke ${r.ziadana_sirka_mm} nie je — použije sa ${rozmerHranyEdge(edge)}.`,
          chyba: false,
        });
      } else if (st === "zla_sirka" || st === "chyba") {
        vety.push({
          text: `Doska ${r.hrubka_dielca_mm} mm: ${CIEL_ROLY[rola]} mm v šírke ${r.ziadana_sirka_mm} nie je ani náhrada.`,
          chyba: true,
        });
      }
    }
  }
  if (vety.length === 0) return null;
  return (
    <div className="space-y-0.5 text-xs" data-testid="suhrn-hran">
      {vety.map((v) => (
        <p
          key={v.text}
          className={cn(
            "flex items-start gap-1",
            v.chyba ? "text-destructive" : "text-warning",
          )}
        >
          <span aria-hidden>!</span>
          <span>{v.text}</span>
        </p>
      ))}
    </div>
  );
}

function EdgeCell({
  edge,
  onClick,
  stav = "ok",
  ciel,
  ziadanaSirka,
}: {
  edge: MaterialSummary | null | undefined;
  /** Bez `onClick` je bunka len na pozretie (slot ešte nie je uložený). */
  onClick?: () => void;
  stav?: string;
  ciel?: string;
  ziadanaSirka?: number | null;
}): JSX.Element {
  const strukturaLabels = useStrukturaLabels();
  const chyba = stav === "zla_sirka" || stav === "chyba";
  if (!edge) {
    const text = chyba
      ? `! v šírke ${ziadanaSirka ?? "?"} nie je`
      : "— (nenastavené)";
    return onClick ? (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "italic hover:underline",
          chyba ? "text-destructive" : "text-muted-foreground",
        )}
        title={
          chyba
            ? `Hrana ${ciel ?? ""} mm v šírke ${ziadanaSirka ?? "?"} v dekore nie je`
            : undefined
        }
      >
        {text}
      </button>
    ) : (
      <span
        className={cn("italic", chyba ? "text-destructive" : "text-muted-foreground")}
      >
        {text}
      </span>
    );
  }
  const struktura = formatStruktura(edge.struktura_povrchu, strukturaLabels);
  const rozmer = rozmerHrany(edge.sirka_mm, edge.hrubka_mm);
  const id = edgeIdOf(edge);
  const nahradena = stav === "nahradena";
  const Obal = onClick ? "button" : "span";
  return (
    <span className="inline-flex items-baseline gap-1">
    <Obal
      {...(onClick ? { type: "button" as const, onClick } : {})}
      className={cn("text-left group", onClick ? "hover:underline" : "")}
      title={
        nahradena
          ? `${ciel ?? ""} mm v šírke ${ziadanaSirka ?? "?"} nie je — použije sa ${rozmer}`
          : edge.nazov
      }
    >
      {nahradena ? (
        <span
          role="img"
          aria-label={`Náhrada ${ciel ?? ""} mm`}
          className="mr-0.5 font-semibold text-warning"
        >
          !
        </span>
      ) : null}
      <span className="font-mono whitespace-nowrap">{rozmer}</span>
      {id ? (
        <span className="font-mono text-[10px] text-muted-foreground ml-1">
          {id}
        </span>
      ) : null}
      <span className="text-muted-foreground"> · </span>
      <span className="text-muted-foreground">{edge.nazov}</span>
      {struktura ? (
        <span className="ml-1 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground">
          {struktura}
        </span>
      ) : null}
    </Obal>
    {edge.eshop_odkaz ? (
      <a
        href={edge.eshop_odkaz}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] text-muted-foreground hover:text-primary"
        title="Otvoriť hranu u dodávateľa"
        aria-label={`Hrana ${rozmer} u dodávateľa`}
      >
        ↗
      </a>
    ) : null}
    </span>
  );
}

/** Modal: vyber hranu z cenníka pre konkrétnu hrúbku + stranu (tenká/hrubá). */
function EdgeOverrideEditor({
  spustiZmenuHran,
  target,
  currentRows,
  onClose,
}: {
  spustiZmenuHran: OknoZmenyHran["spusti"];
  target: { hrubky: number[]; side: EdgeSide } | null;
  currentRows: SlotEdgeRow[];
  onClose: () => void;
}): JSX.Element {
  async function handleSelect(id: string): Promise<void> {
    if (!target) return;
    // Výber hrany sa zavrie hneď — nad ním by sa otváralo okno so zoznamom
    onClose();
    // Celá skupina hrúbok jedným volaním — jeden náhľad, jeden krok späť
    // (do KOLA 222 `Promise.all` volaní, ktoré dielce nemenili vôbec)
    const riadky = target.hrubky.map((hrubka) => {
      const row = currentRows.find((r) => r.hrubka_dielca_mm === hrubka);
      return {
        hrubka_mm: hrubka,
        hrana_tenka_id: target.side === "tenka" ? id : (row?.tenka?.id ?? null),
        hrana_hruba_id: target.side === "hruba" ? id : (row?.hruba?.id ?? null),
        hrana_hruba2_id: target.side === "hruba2" ? id : (row?.hruba2?.id ?? null),
      };
    });
    const label = formatHrubky(target.hrubky);
    const ulozene = await spustiZmenuHran(
      { riadky },
      `zmena hrany ${SIDE_LABELS[target.side]} pre ${label}`,
    );
    if (ulozene) {
      toast({
        title: "Hrana v materiáli zmenená",
        description: `${SIDE_LABELS[target.side]} hrana pre dielce ${label}.`,
      });
    }
  }

  return (
    <HranaPickerDialog
      open={target !== null}
      currentId={null}
      onClose={onClose}
      onSelect={(id) => void handleSelect(id)}
    />
  );
}

// ─── Detekované hrany — list s usage info, exclude, add ─────

/** Mapuje šírku hrany na rozsah hrúbky dielca (inverz thickness_to_edge_width
 * z backendu: 16-18→22, 19-25→28, 26-38→42, 39-43→43). */
function edgeWidthToDielecRange(sirka: number): string {
  if (sirka >= 20 && sirka <= 24) return "16–18 mm";
  if (sirka >= 26 && sirka <= 30) return "19–25 mm";
  if (sirka >= 40 && sirka <= 42) return "26–38 mm";
  if (sirka >= 43 && sirka <= 45) return "39–43 mm";
  return `${sirka} mm`;
}

export function DetectedEdgesSection({
  slotId,
  candidates,
  spustiZmenuHran,
}: {
  slotId: string | undefined;
  candidates: EdgeCandidateRow[];
  /** Kurácia mení hrany, ktoré appka vyberá — ide do dielcov (KOLO 222). */
  spustiZmenuHran: OknoZmenyHran["spusti"];
}): JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const strukturaLabels = useStrukturaLabels();

  async function kuracia(
    materialId: string,
    mode: "add" | "exclude" | null,
    popis: string,
    hotovo: string,
  ): Promise<void> {
    if (!slotId) {
      toast({
        variant: "destructive",
        title: "Najprv ulož materiál",
        description: "Úprava zoznamu hrán je dostupná po uložení materiálu.",
      });
      return;
    }
    const ulozene = await spustiZmenuHran({ kuracia: [{ material_id: materialId, mode }] }, popis);
    if (ulozene) toast({ title: hotovo });
  }

  function handleExclude(materialId: string): Promise<void> {
    return kuracia(materialId, "exclude", "odstránenie hrany zo zoznamu", "Hrana odstránená zo zoznamu");
  }

  function handleUndoExclude(materialId: string): Promise<void> {
    return kuracia(materialId, null, "vrátenie hrany do zoznamu", "Hrana vrátená do zoznamu");
  }

  function handleAddFromCennik(materialId: string): Promise<void> {
    setPickerOpen(false);
    return kuracia(materialId, "add", "pridanie hrany do zoznamu", "Hrana pridaná do zoznamu");
  }

  return (
    <div className="border-t">
      <div className="px-2.5 py-1.5 border-b flex justify-end">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={!slotId}
          className="text-[11px] text-primary hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
          title={slotId ? "Pridať hranu z cenníka" : "Najprv ulož materiál"}
        >
          + Pridať z cenníka
        </button>
      </div>
      <ul className="max-h-56 overflow-y-auto divide-y text-xs">
        {candidates.map((c) => {
          const e = c.edge;
          const u = c.usage;
          const sirka = e.sirka_mm != null ? parseFloat(String(e.sirka_mm)) : 0;
          const id = edgeIdOf(e);
          const dielecRange = sirka > 0 ? edgeWidthToDielecRange(sirka) : "?";
          const isAdded = u.curation === "add";
          return (
            <li
              key={e.id}
              className={
                "flex items-center gap-2 px-2.5 py-1.5 " +
                (isAdded ? "bg-emerald-50" : "")
              }
            >
              <span className="font-mono whitespace-nowrap w-14">
                {rozmerHrany(e.sirka_mm, e.hrubka_mm)}
              </span>
              {id ? (
                <span className="font-mono text-[10px] whitespace-nowrap text-muted-foreground/70 w-16 truncate">
                  {id}
                </span>
              ) : (
                <span className="w-16" />
              )}
              <span className="flex-1 truncate text-muted-foreground" title={e.nazov}>
                {e.nazov}
                {formatStruktura(e.struktura_povrchu, strukturaLabels) ? (
                  <span className="ml-1 rounded bg-muted px-1 py-0.5 text-[10px]">
                    {formatStruktura(e.struktura_povrchu, strukturaLabels)}
                  </span>
                ) : null}
              </span>
              {u.role && (u.used_for_hrubky?.length ?? 0) > 0 ? (
                <span
                  className={
                    "rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap " +
                    (u.role === "tenka"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-amber-100 text-amber-700")
                  }
                  title={`Auto-pickne sa pre dielce: ${(u.used_for_hrubky ?? []).join(", ")} mm`}
                >
                  {u.role} · {dielecRange}
                </span>
              ) : (
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] bg-muted text-muted-foreground whitespace-nowrap"
                  title="V poole ale nie je auto-pick — môžeš ju použiť ako override"
                >
                  v poole · {dielecRange}
                </span>
              )}
              {isAdded ? (
                <span className="rounded bg-emerald-100 px-1 text-[10px] text-emerald-700 whitespace-nowrap">
                  manuálne pridaná
                </span>
              ) : null}
              {slotId ? (
                isAdded ? (
                  <button
                    type="button"
                    onClick={() => void handleUndoExclude(e.id)}
                    className="text-[10px] text-muted-foreground hover:text-destructive"
                    title="Odstrániť (vrátiť späť na auto-detect)"
                  >
                    ↺
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleExclude(e.id)}
                    className="text-[12px] text-muted-foreground hover:text-destructive"
                    title="Odstrániť hranu zo zoznamu"
                  >
                    ×
                  </button>
                )
              ) : null}
            </li>
          );
        })}
        {candidates.length === 0 ? (
          <li className="px-2.5 py-4 text-center text-muted-foreground italic">
            Žiadne detekované hrany. Pridaj manuálne cez "+ Pridať z cenníka".
          </li>
        ) : null}
      </ul>

      <HranaPickerDialog
        open={pickerOpen}
        currentId={null}
        onClose={() => setPickerOpen(false)}
        onSelect={(id) => void handleAddFromCennik(id)}
      />
    </div>
  );
}



// ─── Karta materiálu v paneli (KOLO 228) ────────────────────

/**
 * Materiál, ktorý sa olepuje — má zmysel okno Hrany. Dĺžkový sa len
 * kráti, sklo a masív sa neolepujú (rozhoduje druh a kategória
 * materiálu, nie príznak `je_auto_hrany`, ktorý import vypína).
 */
export function slotMaHrany(slot: MaterialSlot): boolean {
  const m = slot.material;
  if (!m) return false;
  if (m.druh === "dlzkovy") return false;
  if (jeSkloMaterial(m.kategoria)) return false;
  return !m.masiv;
}

const CHYBA_HRANY: Record<string, { text: string; title: string; chyba: boolean }> = {
  zla_sirka: {
    text: "! úzka",
    title: "Hrana je úzka na hrúbku dosky — vyber širšiu v okne Hrany.",
    chyba: true,
  },
  chyba: {
    text: "! chýba",
    title: "Dekor nemá hranu v potrebnej šírke — vyber hranu v okne Hrany.",
    chyba: true,
  },
  nahradena: {
    text: "! iná hrúbka",
    title: "Hrana v potrebnej šírke v tejto hrúbke nie je — použije sa iná.",
    chyba: false,
  },
};

/** Najhorší stav riadku: chýba / úzka pred náhradou. */
function stavRiadku(r: SlotEdgeRow): string | null {
  const stavy = [r.stav_tenka, r.stav_hruba, r.stav_hruba2];
  for (const st of ["chyba", "zla_sirka", "nahradena"]) {
    if (stavy.includes(st)) return st;
  }
  return null;
}

/**
 * Riadok „Hrany 18 mm 22×0,8 · 22×2 · 36 mm ! úzka" na karte materiálu.
 *
 * Do KOLA 228 karta ukazovala „+ auto hrany (tenká: 22x0.5)" a „!" len
 * pri `je_auto_hrany` — materiál z importu z TopSolidu ho má vypnutý,
 * takže úzka hrana na ňom nesvietila. Teraz každá **použitá** hrúbka
 * (hrúbka dosky a dielcov materiálu) so svojimi hranami a stavom.
 */
export function HranyNaKarte({ slotId }: { slotId: string }): JSX.Element | null {
  const q = useSlotEdgesByThickness(slotId);
  const rows = (q.data?.data?.rows ?? []).filter((r) => r.pouzita);
  if (rows.length === 0) return null;
  return (
    <div
      className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[11px]"
      data-testid="hrany-na-karte"
    >
      <span className="text-muted-foreground">Hrany</span>
      {rows.map((r) => {
        const rozmery = [r.tenka, r.hruba, r.hruba2]
          .filter((e): e is MaterialSummary => !!e)
          .map((e) => rozmerHrany(e.sirka_mm, e.hrubka_mm));
        const unikatne = [...new Set(rozmery)];
        const stav = stavRiadku(r);
        const znak = stav ? CHYBA_HRANY[stav] : undefined;
        return (
          <span key={r.hrubka_dielca_mm} className="whitespace-nowrap">
            {r.hrubka_dielca_mm} mm{" "}
            <span className="font-mono text-muted-foreground">
              {unikatne.length > 0 ? unikatne.join(" · ") : "—"}
            </span>
            {znak ? (
              <span
                role="img"
                aria-label="Hrany materiálu treba skontrolovať"
                title={znak.title}
                className={cn(
                  "ml-1 font-semibold",
                  znak.chyba ? "text-destructive" : "text-warning",
                )}
              >
                {znak.text}
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
